import { Pill, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { ChevronRightOutlined, ExpandMoreOutlined, HistoryOutlined } from '@mui/icons-material';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import {
  ActionsCell,
  Card,
  Cron,
  ExecutionId,
  ExpandIcon,
  HeaderLabels,
  List,
  NonToggleText,
  RowMain,
  RunHistoryLink,
  WorkflowTypeCell
} from './BrandWorkflows.styles';
import type { WorkflowExecutionRow } from './BrandWorkflows.types';
import RetryFromFailedButton from './RetryFromFailedButton';
import WorkflowTasksDetails from './WorkflowTasksDetails';
import { workflowDisplayName } from './workflowCatalog';
import {
  executionDurationStart,
  executionPillVariant,
  formatWorkflowDuration,
  formatWorkflowTimestamp,
  shortenExecutionId
} from './workflowFormatters';

const ScheduleContent = ({ scheduler }: { scheduler: WorkflowExecutionRow['scheduler'] }) => {
  if (!scheduler) return null;
  if (!scheduler.isActive) return <Pill label="Paused" variant="default" />;
  return <Cron>{scheduler.cronExpression}</Cron>;
};

type WorkflowExecutionListBaseProps = {
  rows: WorkflowExecutionRow[];
  emptyText: string;
  loading?: boolean;
  // The run-history link only belongs on the Latest page; its `to` is relative, so it
  // only resolves from the `workflows` route
  showRunHistoryLink: boolean;
};

// Retry only belongs on the Latest page; when it's on, the in-flight id and handler are
// required together (retryingExecutionId disables retry while another run is retrying)
type WorkflowExecutionRetryProps =
  | {
      showRetry: true;
      retryingExecutionId: string | null;
      onRetryFromFailed: (executionId: string) => void;
    }
  | { showRetry?: false; retryingExecutionId?: never; onRetryFromFailed?: never };

type WorkflowExecutionListProps = WorkflowExecutionListBaseProps & WorkflowExecutionRetryProps;

const WorkflowExecutionList = ({
  rows,
  emptyText,
  loading = false,
  showRetry = false,
  showRunHistoryLink,
  retryingExecutionId,
  onRetryFromFailed
}: WorkflowExecutionListProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const copyToClipboard = useCopyToClipboard();

  const onToggleExpand = (executionId: string) => {
    setExpandedIds(previous => {
      const updated = new Set(previous);
      if (updated.has(executionId)) updated.delete(executionId);
      else updated.add(executionId);
      return updated;
    });
  };

  return (
    <List>
      {rows.length === 0 && !loading && <Text color="neutral60">{emptyText}</Text>}
      {rows.length > 0 && (
        <HeaderLabels>
          <span>Status</span>
          <span>Workflow Type</span>
          <span>ID</span>
          <span>Started</span>
          <span>Duration</span>
          <span>Trigger</span>
          <span>Schedule</span>
        </HeaderLabels>
      )}
      {rows.map(row => {
        const expanded = expandedIds.has(row.executionId);

        const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
          const { target } = event;
          // Clicks on actions and copyable fields shouldn't toggle the row
          if (target instanceof Element && target.closest('[data-no-row-toggle]')) return;
          onToggleExpand(row.executionId);
        };

        return (
          <Card key={row.executionId} failed={row.status === 'Failure'}>
            <RowMain
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} run ${row.executionId} details`}
              onClick={onRowClick}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleExpand(row.executionId);
                }
              }}
            >
              <div>
                <Pill
                  label={row.status}
                  variant={executionPillVariant(row.status, row.isFirstRun ?? false)}
                />
              </div>
              {/* Tooltip sits on the cell, not the Text, so it has a ref-forwarding host to
                  anchor to; it doubles as the reveal for a name the ellipsis has cut off */}
              <Tooltip title={row.workflowType}>
                <WorkflowTypeCell>
                  <Text variant="body1Semibold" ellipsis>
                    {workflowDisplayName(row.workflowType)}
                  </Text>
                </WorkflowTypeCell>
              </Tooltip>
              <Tooltip title={row.executionId}>
                <ExecutionId
                  data-no-row-toggle
                  onClick={() => copyToClipboard(row.executionId)}
                  aria-label={`Copy execution ID ${row.executionId}`}
                >
                  {shortenExecutionId(row.executionId)}
                </ExecutionId>
              </Tooltip>
              <NonToggleText data-no-row-toggle>
                <Text variant="body2">{formatWorkflowTimestamp(row.startedAt)}</Text>
              </NonToggleText>
              <NonToggleText data-no-row-toggle>
                <Text variant="body2">
                  {formatWorkflowDuration(executionDurationStart(row), row.endedAt)}
                </Text>
              </NonToggleText>
              <div>
                <Pill label={row.triggerType} variant="default" />
              </div>
              <div>
                <ScheduleContent scheduler={row.scheduler} />
              </div>
              <ActionsCell data-no-row-toggle>
                {showRetry && row.status === 'Failure' && (
                  <RetryFromFailedButton
                    workflowType={row.workflowType}
                    disabled={retryingExecutionId != null}
                    onConfirm={() => onRetryFromFailed?.(row.executionId)}
                  />
                )}
                {showRunHistoryLink ? (
                  <Tooltip title="Run history">
                    {/* `to` is the raw workflow type — the route keys off it, not the display name */}
                    <RunHistoryLink
                      to={row.workflowType}
                      aria-label={`View ${row.workflowType} run history`}
                    >
                      <HistoryOutlined />
                    </RunHistoryLink>
                  </Tooltip>
                ) : null}
              </ActionsCell>
              <ExpandIcon>
                {expanded ? <ExpandMoreOutlined /> : <ChevronRightOutlined />}
              </ExpandIcon>
            </RowMain>
            {expanded && <WorkflowTasksDetails executionId={row.executionId} />}
          </Card>
        );
      })}
    </List>
  );
};

export default WorkflowExecutionList;
